import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Checkbox, FormControl, FormControlLabel, FormGroup, FormLabel } from '@material-ui/core';
import { Publish } from '@material-ui/icons';
import { LoadingButton } from 'components/buttons';
import { CustomLabel } from 'components/inputs';
import { ApiLender, SelectOption } from 'core/types';
import { gatherOffers as gatherOffersApi } from 'http/admin';
import { useAdmin } from 'store/admin/hooks';
import { useLenders } from 'store/lenders/hooks';
import { toTitleCase } from 'utils';
import useStyles from './MultiGatherOffers.styles';

// Helper to format lender names for the success message
const getLendersStr = (apiLenders: ApiLender[]) => {
  const lendersLength = apiLenders.length;
  return apiLenders
    .map((lender, index) => {
      if (lendersLength > 1) {
        if (index < lendersLength - 2) {
          return `${toTitleCase(lender)}, `;
        }
        if (index < lendersLength - 1) {
          return `${toTitleCase(lender)} and  `;
        }
      }
      return toTitleCase(lender);
    })
    .join('');
};

interface MultiGatherOffersProps {
  companyIds: string[];
}

const MultiGatherOffers: FC<MultiGatherOffersProps> = ({ companyIds }) => {
  const [loading, setLoading] = useState(false);
  const [apiLenders, setApiLenders] = useState<ApiLender[]>([]);

  const classes = useStyles();
  const { t } = useTranslation();
  const { setError, setSuccess } = useAdmin();
  const { allLenders } = useLenders();

  // Determine if we have items to process
  const hasSelection = companyIds.length > 0;

  // Build the list of available lenders from the store
  const availableApiLenders = useMemo(() => {
    return Object.values(ApiLender).reduce(
      (acc: SelectOption<ApiLender>[], val: ApiLender): SelectOption<ApiLender>[] => {
        const apiLender = allLenders.find((e) => e.name === val);
        if (!apiLender) return acc;
        acc.push({ label: apiLender.displayed_name, value: val });
        return acc;
      },
      [],
    );
  }, [allLenders]);

  const onChange = (value: ApiLender) => {
    setApiLenders((prevApiLenders) => {
      const alreadySelected = prevApiLenders.find((lender) => lender === value);
      if (alreadySelected) return prevApiLenders.filter((lender) => lender !== value);
      return [...prevApiLenders, value];
    });
  };

  const onSubmit = async () => {
    if (!hasSelection) return;

    setLoading(true);
    try {
      // PROMISE.ALL IMPLEMENTATION
      // We map over the array of companyIds and trigger the existing API call for each
      await Promise.all(companyIds.map((id) => gatherOffersApi(id, apiLenders)));

      const successMessage = t('pages.lead.admin.gatherOffers.messages.success', {
        lenders: getLendersStr(apiLenders),
      });

      // Optionally append how many leads were updated
      setSuccess(`${successMessage} (${companyIds.length} leads updated)`);
    } catch (err) {
      setError((err as Error)?.message ?? true);
    }
    setLoading(false);
  };

  return (
    <Box paddingRight="8px">
      <FormControl component="fieldset" disabled={!hasSelection}>
        <FormLabel component={() => <CustomLabel title={t('pages.lead.admin.gatherOffers.title')} />} />
        <FormLabel component={() => <CustomLabel title={t('pages.lead.admin.gatherOffers.subtitle')} />} />

        <FormGroup>
          {availableApiLenders.map((option) => (
            <FormControlLabel
              key={`${option.value}`}
              value={option.value}
              control={
                <Checkbox
                  checked={apiLenders.includes(option.value)}
                  onChange={() => onChange(option.value)}
                  color="primary"
                />
              }
              label={option.label}
              className={classes.controlLabel}
            />
          ))}
        </FormGroup>
      </FormControl>

      <LoadingButton
        variant="contained"
        color="primary"
        disableElevation
        className={classes.button}
        onClick={onSubmit}
        // Button is disabled if no lenders selected OR no companies passed in props
        disabled={!apiLenders.length || !hasSelection}
        loading={loading}
        startIcon={<Publish />}
      >
        {t('pages.lead.admin.actions.gatherOffers')}
      </LoadingButton>
    </Box>
  );
};

export default MultiGatherOffers;
